import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../lib/firebase/config';
import FormInput from '../components/auth/FormInput';

const setPasswordSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

export default function SetPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidCode, setIsValidCode] = useState(true);
  const [email, setEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(setPasswordSchema),
  });

  useEffect(() => {
    const verifyCode = async () => {
      try {
        // Get the action code from the URL
        const urlParams = new URLSearchParams(window.location.search);
        const actionCode = urlParams.get('oobCode');

        if (!actionCode) {
          setIsValidCode(false);
          return;
        }

        // Verify the password reset code
        console.log('[SetPassword] Verifying password reset code:', actionCode);
        const email = await verifyPasswordResetCode(auth, actionCode);
        setEmail(email);
        console.log('[SetPassword] Password reset code valid for email:', email);
      } catch (err) {
        setIsValidCode(false);
        setError('This password reset link is invalid or has expired.');
        console.error('[SetPassword] Invalid or expired reset link:', err);
      }
    };

    verifyCode();
  }, []);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      // Get the action code from the URL
      const urlParams = new URLSearchParams(window.location.search);
      const actionCode = urlParams.get('oobCode');

      if (!actionCode) {
        setError('Invalid reset link.');
        console.error('[SetPassword] No action code found in URL.');
        setIsLoading(false);
        return;
      }

      // Confirm the password reset
      console.log('[SetPassword] Confirming password reset for email:', email);
      await confirmPasswordReset(auth, actionCode, data.password);
      setIsSuccess(true);
      console.log('[SetPassword] Password reset successful.');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
    } catch (err) {
      let message = 'An error occurred. Please try again.';
      if (err && err.code === 'auth/expired-action-code') {
        message = 'This password reset link has expired.';
      } else if (err && err.code === 'auth/invalid-action-code') {
        message = 'This password reset link is invalid.';
      } else if (err && err.code === 'auth/user-disabled') {
        message = 'This account has been disabled.';
      } else if (err && err.code === 'auth/user-not-found') {
        message = 'No account found for this reset link.';
      } else if (err && err.message) {
        message += ` [${err.message}]`;
      }
      setError(message);
      console.error('[SetPassword] Error confirming password reset:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isValidCode) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Invalid Reset Link</h2>
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                This password reset link is invalid or has expired
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  Please request a new password reset link from the{' '}
                  <a
                    href="/forgot-password"
                    className="font-medium text-red-600 hover:text-red-500"
                  >
                    forgot password page
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Password Reset Successful</h2>
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Your password has been reset
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  You can now sign in with your new password. Redirecting to login...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Set New Password</h2>

      <p className="text-sm text-gray-600 mb-6">
        Please enter a new password for {email}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <FormInput
          label="New password"
          id="password"
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <FormInput
          label="Confirm new password"
          id="confirmPassword"
          type="password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orrekie-600 hover:bg-orrekie-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orrekie-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Resetting password...' : 'Reset password'}
          </button>
        </div>
      </form>
    </div>
  );
} 